import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Copy, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import AuthLayout from './AuthLayout';
import { authService } from '../../services/authService';

const TwoFactorSetupPage: React.FC = () => {
  const [step, setStep] = useState<'setup' | 'verify'>('setup');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Check if user is authenticated
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }

    // Load 2FA setup data
    loadSetupData();
  }, [navigate]);

  const loadSetupData = async () => {
    try {
      const setupData = await authService.setup2FA();
      setSecretKey(setupData.secret);
      setQrCode(setupData.qrCode);
    } catch (error) {
      toast.error('Failed to load 2FA setup data');
    }
  };

  const handleCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(secretKey);
      setCopied(true);
      toast.success('Secret key copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy secret key');
    }
  };

  const handleInputChange = (index: number, value: string) => {
    if (value.length > 1) return; // Only allow single digit
    if (!/^\d*$/.test(value)) return; // Only allow numbers

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all fields are filled
    if (newCode.every(digit => digit !== '') && newCode.join('').length === 6) {
      handleVerifyCode(newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      // Focus previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);

    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setCode(newCode);
      setError('');
      handleVerifyCode(pastedData);
    }
  };

  const handleVerifyCode = async (codeString?: string) => {
    const verificationCode = codeString || code.join('');

    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setIsLoading(true);
    try {
      const success = await authService.enable2FA(verificationCode);
      if (success) {
        toast.success('Two-factor authentication enabled successfully!');
        navigate('/dashboard');
      } else {
        setError('Invalid verification code. Please try again.');
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      setError('Setup failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (step === 'setup') {
    return (
      <AuthLayout
        title="Set up Two-Factor Authentication"
        subtitle="Secure your account with an additional layer of protection"
      >
        <div className="space-y-6">
          {/* 2FA Icon */}
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
              <Shield className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
          </div>

          {/* Instructions */}
          <div className="text-center space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Step 1: Scan QR Code
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Use your authenticator app (Google Authenticator, Authy, etc.) to scan this QR code:
            </p>
          </div>

          {/* QR Code */}
          <div className="flex justify-center">
            <div className="bg-white p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <img
                src={qrCode}
                alt="2FA QR Code"
                className="w-48 h-48 bg-gray-100 dark:bg-gray-800 rounded"
              />
            </div>
          </div>

          {/* Manual Entry */}
          <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
              Can't scan? Enter this code manually:
            </p>
            <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <code className="flex-1 text-sm font-mono text-gray-900 dark:text-white break-all">
                {secretKey}
              </code>
              <button
                onClick={handleCopySecret}
                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                title="Copy secret key"
              >
                {copied ? (
                  <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                ) : (
                  <Copy className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                )}
              </button>
            </div>
          </div>

          {/* Continue Button */}
          <div>
            <button
              onClick={() => {
                setStep('verify');
                setTimeout(() => inputRefs.current[0]?.focus(), 100);
              }}
              className="
                group relative w-full flex justify-center py-3 px-4 border border-transparent 
                text-sm font-medium rounded-md text-white bg-black hover:bg-gray-800 
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500
                dark:bg-gray-900 dark:hover:bg-gray-800 dark:focus:ring-gray-400
                transition-colors duration-200
              "
            >
              Continue to Verification
            </button>
          </div>

          {/* Back Link */}
          <div className="text-center">
            <Link
              to="/dashboard"
              className="
                inline-flex items-center gap-2 text-sm font-medium text-gray-600 
                hover:text-gray-900 dark:text-gray-400 dark:hover:text-white
              "
            >
              <ArrowLeft size={16} />
              Back to Dashboard
            </Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Verify Your Setup"
      subtitle="Enter the 6-digit code from your authenticator app"
    >
      <div className="space-y-6">
        {/* 2FA Icon */}
        <div className="flex justify-center">
          <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
            <Shield className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
        </div>

        {/* Instructions */}
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Step 2: Enter Verification Code
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Open your authenticator app and enter the 6-digit code for Fibre
          </p>
        </div>

        {/* Code Input */}
        <div>
          <div className="flex justify-center gap-3 mb-4">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className={`
                  w-12 h-12 text-center text-xl font-bold border-2 rounded-lg
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                  dark:bg-gray-700 dark:border-gray-600 dark:text-white
                  ${error
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 dark:border-gray-600'
                  }
                  transition-colors duration-200
                `}
                disabled={isLoading}
              />
            ))}
          </div>
          {error && (
            <p className="text-sm text-red-600 dark:text-red-400 text-center">{error}</p>
          )}
        </div>

        {/* Submit Button */}
        <div>
          <button
            onClick={() => handleVerifyCode()}
            disabled={isLoading || code.some(digit => digit === '')}
            className="
              group relative w-full flex justify-center py-3 px-4 border border-transparent 
              text-sm font-medium rounded-md text-white bg-black hover:bg-gray-800 
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500
              disabled:opacity-50 disabled:cursor-not-allowed
              dark:bg-gray-900 dark:hover:bg-gray-800 dark:focus:ring-gray-400
              transition-colors duration-200
            "
          >
            {isLoading ? 'Enabling 2FA...' : 'Enable Two-Factor Authentication'}
          </button>
        </div>

        {/* Back Button */}
        <div className="text-center">
          <button
            onClick={() => setStep('setup')}
            className="
              inline-flex items-center gap-2 text-sm font-medium text-gray-600 
              hover:text-gray-900 dark:text-gray-400 dark:hover:text-white
            "
          >
            <ArrowLeft size={16} />
            Back to QR Code
          </button>
        </div>

      </div>
    </AuthLayout>
  );
};

export default TwoFactorSetupPage;
